import React, { Component } from "react";
import './pdf-viewer.scss';
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
import Loading from "../loading/loading";
import { getImageSource } from "../../../utility/image-source";
import { ImageConstants } from "../../../constants/constants";
import { CountdownCircleTimer } from 'react-countdown-circle-timer'

const workerPath = process.env.PUBLIC_URL ? process.env.PUBLIC_URL + "/pdf.worker.min.js" : "/pdf.worker.min.js";
pdfjsLib.GlobalWorkerOptions.workerSrc = workerPath;

class PdfViewer extends Component {
    constructor(props) {
        super(props);
        this.state = {
            pdf: null,
            pageNumber: 1,
            numPages: 0,
            isLoading: false,
            isRendering: false,
            scale: 1,
            pdfNotFound: false,
            countDownTime: this.props.countDownTime || 1,
            nextButtonEnabled: this.props.attemptNumber >= 2,
        };
        this.canvasRef = React.createRef();
        this.containerRef = React.createRef();
        this.pdfContainerRef = React.createRef();
    }

    componentDidMount() {
        if (this.props.pdfUrl) {
            this.setState({ isLoading: true, pdfNotFound: false });
            this.loadPdf(this.props.pdfUrl);
        } else {
            this.setState({ pdfNotFound: true });
        }
        window.addEventListener("resize", this.handleResize);
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.handleResize);
    }

    componentDidUpdate(prevProps) {
        if (this.props.attemptNumber !== prevProps.attemptNumber) {
            if (this.props.attemptNumber >= 2) {
                this.setState({ nextButtonEnabled: true });
            }
        }
    }

    handleResize = () => {
        const { pdf, pageNumber } = this.state;
        if (pdf) {
            this.renderPage(pdf, pageNumber);
        }
    };

    zoomIn = () => {
        this.setState(prevState => ({ scale: prevState.scale + 0.2 }), () => {
            const { pdf, pageNumber } = this.state;
            this.renderPage(pdf, pageNumber);
        });
    };

    enableNextButton = () => {
        this.setState({ nextButtonEnabled: true });
        if (this.props.delegate && this.props.delegate.enableNextButton) {
            this.props.delegate.enableNextButton();
        }
    }

    zoomOut = () => {
        this.setState(prevState => ({ scale: Math.max(0.4, prevState.scale - 0.2) }), () => {
            const { pdf, pageNumber } = this.state;
            this.renderPage(pdf, pageNumber);
        });
    };



    loadPdf = async (pdfUrl) => {
        try {
            const loadingTask = pdfjsLib.getDocument({ url: pdfUrl });
            const pdf = await loadingTask.promise;
            this.setState(
                { pdf: pdf, numPages: pdf.numPages },
                () => {
                    this.setState({ isLoading: false }, () => {
                        // Small delay ensures the container is fully mounted and dimensions are available
                        setTimeout(() => {
                            this.renderPage(pdf, 1);
                        }, 100);
                    });
                    if (this.props.delegate && this.props.delegate.startPdfActivity) {
                        this.props.delegate.startPdfActivity(this.props.pdfTitle, this.props.pdfNodeId, this.props.chapterKey, this.props.conceptKey);
                    }
                }
            );
        } catch (err) {
            console.error("PDF load error", err);
            this.setState({ isLoading: false });
        }
    };

    renderPage = async (pdf, pageNumber) => {
        if (!pdf || !this.containerRef.current || !this.canvasRef.current) return;

        if (this.renderTask) {
            try {
                this.renderTask.cancel();
            } catch (err) {
                // Ignore cancel errors
            }
        }

        this.setState({ isRendering: true });
        this.isRenderingSync = true;

        try {
            const page = await pdf.getPage(pageNumber);

            let containerWidth = 800;
            let containerHeight = 600;

            if (this.pdfContainerRef.current) {
                containerWidth = this.pdfContainerRef.current.offsetWidth - 20;
                containerHeight = this.pdfContainerRef.current.offsetHeight - 20;
            }

            const effectiveContainerWidth = containerWidth > 0 ? containerWidth : 800;
            const effectiveContainerHeight = containerHeight > 0 ? containerHeight : 600;

            const viewport = page.getViewport({ scale: 1 });

            const scaleWidth = effectiveContainerWidth / viewport.width;
            const scaleHeight = effectiveContainerHeight / viewport.height;

            const scale = Math.min(scaleWidth, scaleHeight);
            const effectiveScale = ((scale > 0 && scale < Infinity) ? scale : 1) * this.state.scale;
            const scaledViewport = page.getViewport({ scale: effectiveScale });

            const canvas = this.canvasRef.current;
            const context = canvas.getContext("2d");
            const outputScale = window.devicePixelRatio || 1;

            canvas.width = Math.floor(scaledViewport.width * outputScale);
            canvas.height = Math.floor(scaledViewport.height * outputScale);
            canvas.style.width = Math.floor(scaledViewport.width) + "px";
            canvas.style.height = Math.floor(scaledViewport.height) + "px";

            // Add animation class for smooth transition
            canvas.classList.remove('fade-in');
            void canvas.offsetWidth; // trigger reflow
            canvas.classList.add('fade-in');

            context.setTransform(outputScale, 0, 0, outputScale, 0, 0);

            this.renderTask = page.render({
                canvasContext: context,
                viewport: scaledViewport
            });
            await this.renderTask.promise;
        } catch (e) {
            if (e.name !== 'RenderingCancelledException') {
                console.error("Page render error", e);
            }
        } finally {
            this.setState({ isRendering: false });
            this.isRenderingSync = false;
        }
    };

    nextPage = () => {
        if (this.isRenderingSync) return;
        const { pageNumber, pdf, numPages } = this.state;
        if (pageNumber >= numPages) return;
        const newPage = pageNumber + 1;
        this.setState({ pageNumber: newPage, nextButtonEnabled: this.props.attemptNumber >= 2 });
        this.renderPage(pdf, newPage);

        if (this.props.delegate && this.props.delegate.updatePdfActivity) {
            if (newPage === numPages) {
                this.props.delegate.updatePdfActivity("COMPLETED");
            } else {
                this.props.delegate.updatePdfActivity("IN_PROGRESS");
            }
        }
    };

    prevPage = () => {
        if (this.isRenderingSync) return;
        const { pageNumber, pdf } = this.state;
        if (pageNumber <= 1) return;
        const newPage = pageNumber - 1;
        this.setState({ pageNumber: newPage, nextButtonEnabled: this.props.attemptNumber >= 2 });
        this.renderPage(pdf, newPage);
    };

    closePdfFlow = () => {
        if (this.props.delegate && this.props.delegate.hidePdfViewer) {
            this.props.delegate.hidePdfViewer();
        }
    }

    finishPdfFlow = () => {
        const { numPages } = this.state;
        if (this.props.delegate && this.props.delegate.finishPdfConcept) {
            this.props.delegate.finishPdfConcept({ totalPages: numPages });
        }
    }

    render() {
        const { pageNumber, numPages, pdfNotFound } = this.state;
        return (
            <div className="clicker-revision-experience" ref={this.containerRef}>
                {pdfNotFound ? (
                    <>
                        <div className="pdf-viewer-header">
                            <div className="title">{this.props.pdfTitle || ' '}</div>
                            <img className="close-icon"
                                onClick={this.closePdfFlow}
                                src={getImageSource(ImageConstants.COUNTRY_CLOSE_ICON)}
                                alt="Close"
                            />
                        </div>
                        <div className="pdf-viewer-body" style={{ alignItems: 'center' }}>
                            <div className="pdf-not-found-message" style={{ textAlign: 'center', fontSize: '20px', fontWeight: 'bold', color: '#555' }}>
                                No PDF found for this concept or chapter.
                            </div>
                        </div>
                    </>
                ) : this.state.isLoading ? (
                    <Loading />
                ) : (
                    <>
                        <div className="pdf-viewer-header">
                            <div className="title">{this.props.pdfTitle || ' '}</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <button className="btn btn-sm btn-outline-secondary" onClick={this.zoomOut} disabled={this.state.scale <= 0.4} style={{ padding: '0 8px', borderRadius: '4px', border: '1px solid #ccc', backgroundColor: '#fff', cursor: 'pointer', fontSize: '18px', lineHeight: '24px' }}>-</button>
                                    <span style={{ minWidth: '40px', textAlign: 'center', fontWeight: 'bold' }}>{Math.round(this.state.scale * 100)}%</span>
                                    <button className="btn btn-sm btn-outline-secondary" onClick={this.zoomIn} style={{ padding: '0 8px', borderRadius: '4px', border: '1px solid #ccc', backgroundColor: '#fff', cursor: 'pointer', fontSize: '18px', lineHeight: '24px' }}>+</button>
                                </div>
                                <img className="close-icon"
                                    onClick={this.closePdfFlow}
                                    src={getImageSource(ImageConstants.COUNTRY_CLOSE_ICON)}
                                    alt="Close"
                                />
                            </div>
                        </div>

                        <div ref={this.pdfContainerRef} className="pdf-viewer-body" style={{ position: 'relative' }}>
                            {this.state.isRendering && (
                                <div style={{
                                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                    display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 5
                                }}>
                                    <Loading />
                                </div>
                            )}
                            <canvas ref={this.canvasRef}></canvas>
                        </div>
                        <div className="pdf-viewer-footer">
                            <button className="btn btn-primary pdf-btn" onClick={this.prevPage} disabled={pageNumber <= 1 || this.state.isRendering}>
                                Previous
                            </button>
                            <span className="page-indicator">
                                Page {pageNumber} of {numPages}
                            </span>
                            {pageNumber >= numPages ? (
                                <button className="btn btn-primary pdf-btn" style={{ backgroundColor: '#28a745', borderColor: '#28a745' }} onClick={this.finishPdfFlow} disabled={this.state.isRendering}>
                                    Finish
                                </button>
                            ) : (
                                <>
                                    <div className="pdf-btn-wrapper">
                                        <button
                                            className="btn btn-primary pdf-btn"
                                            onClick={this.nextPage}
                                            disabled={pageNumber >= numPages || this.state.isRendering || !this.state.nextButtonEnabled}
                                        >
                                            Next
                                        </button>
                                        {!this.state.nextButtonEnabled && (
                                            <>
                                                <div className="cw-tooltip">
                                                    Read it — Next unlocks when timer finishes!
                                                </div>
                                                <div className="timer-wrapper" id="countdown-id">
                                                    <div className="timer-placement">
                                                        <CountdownCircleTimer
                                                            key={pageNumber}
                                                            size={24}
                                                            strokeWidth={5}
                                                            trailStrokeWidth={5}
                                                            strokeLinecap="round"
                                                            trailColor='#607D8B'
                                                            isPlaying
                                                            duration={this.state.countDownTime}
                                                            colors={["#1CEA24", "#F7B801", "#A30000", "#A30000"]}
                                                            colorsTime={[
                                                                this.state.countDownTime,
                                                                Math.round(this.state.countDownTime * 0.6),
                                                                Math.round(this.state.countDownTime * 0.3),
                                                                0
                                                            ]}
                                                            onComplete={() => ({ shouldRepeat: false, delay: 1 }, this.enableNextButton())}
                                                        />
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </>
                )}
            </div>
        );
    }
}

export default PdfViewer;
